import { useLocation } from 'react-router';
import { useTracker } from '@archipro-website/tracker';
import { markRestoreAtTopAfterLanding } from '~/modules/root/component/scroll-restoration/restore-at-top';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { CategoryNeighbour } from '@modules/product/util/fetch-category-neighbours.server';
import {
    ISLAND_GLASS_DARK,
    ISLAND_GLASS_LAYOUT_ID,
} from '~/ui-rocco/pages/detail-shared/magic-island/styles';
import { nextTurnData } from '~/ui-rocco/pages/detail-shared/next-turn-event';

/** The shell's own entrance spring, so this surface settles with the pill. */
const POP = { type: 'spring', stiffness: 520, damping: 21, mass: 1 } as const;

interface NextProductIslandContentProps {
    next: CategoryNeighbour;
}

/**
 * The island at the foot of the page: the next product in the category, as a
 * thumbnail with its name.
 *
 * Taller than the pill modes above it on purpose. By this point the reader has
 * finished the product and the question is what to look at next, so the island
 * stops being a control and becomes the answer, which needs the photograph to
 * be big enough to recognise.
 *
 * The glass carries the shared layout id and this mode registers on the `pill`
 * surface, which is what makes the pill morph into this rather than exit and
 * let it enter. Its own surface key put the two through the shell's
 * `AnimatePresence mode="wait"`, where the pill has to finish leaving before
 * anything arrives, and a mode that flips back and forth during a scroll can
 * leave that sequence unfinished with nothing on screen.
 */
export const NextProductIslandContent = ({
    next,
}: NextProductIslandContentProps) => {
    const reduce = useReducedMotion() ?? false;
    const location = useLocation();
    const { log } = useTracker();

    return (
        <motion.div
            layout={!reduce}
            layoutId={reduce ? undefined : ISLAND_GLASS_LAYOUT_ID}
            transition={POP}
            className={cn(ISLAND_GLASS_DARK, 'rounded-full')}
        >
            <RoccoLink
                to={next.link}
                prefetch="intent"
                // The scroll-driven turn sends the page it left back to its
                // top on Back; taking the same offer from the island left the
                // reader mid-page instead, so the same round trip behaved two
                // ways depending on which control they used.
                // Taking the offer here is the same deliberate turn as
                // clicking the growing section, and reports as one.
                onClick={() => {
                    markRestoreAtTopAfterLanding(location.key);
                    log('NextProductTurn', {
                        url: new URL(window.location.href),
                        data: nextTurnData('product', next.link, 'click'),
                    });
                }}
                aria-label={`Next product: ${next.title}`}
                className="group flex items-center gap-[0.875rem] p-[0.625rem] no-underline md:gap-4 md:p-3"
            >
                {next.image && next.imageContained ? (
                    // A cut-out is drawn to fit and carries its own white
                    // padding, so it needs the same pairing the tiles use: the
                    // clear-cut fill behind it and multiply over that, which
                    // takes the padding out and leaves the object on the plate.
                    // The plate is what the letterboxing shows, so the slot
                    // reads as a small light card rather than as bands. It has
                    // to be a plate rather than the glass itself: multiplied
                    // straight onto the dark island the cut-out renders black.
                    // `isolate` keeps the blend on the plate for the same
                    // reason.
                    <span className="isolate flex size-[2.75rem] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--color-clearcut-fill)] md:size-14">
                        <img
                            src={next.image}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="size-full object-contain mix-blend-multiply"
                        />
                    </span>
                ) : next.image ? (
                    // A photograph fills the slot.
                    <img
                        src={next.image}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        className="size-[2.75rem] shrink-0 rounded-full bg-white/10 object-cover md:size-14"
                    />
                ) : (
                    <span className="size-[2.75rem] shrink-0 rounded-full bg-white/10 md:size-14" />
                )}

                <span className="flex min-w-0 flex-col gap-1 pr-2">
                    <span className="font-sans text-sm leading-none font-normal tracking-[-0.01em] text-white/60">
                        Next Product
                    </span>
                    <span className="line-clamp-1 max-w-[20rem] font-sans text-base leading-[1.3] font-medium tracking-[-0.02em] text-white md:text-[1.25rem]">
                        {next.title}
                    </span>
                </span>

                <span className="flex size-[2.75rem] shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors group-hover:bg-white/25 md:size-14">
                    <ArrowRight aria-hidden="true" className="size-5" />
                </span>
            </RoccoLink>
        </motion.div>
    );
};
